/**
 * Environment configuration
 * Centralizes all environment variables for type-safe access.
 *
 * Runtime injection strategy (production):
 *   The container entrypoint (iac/entrypoint.sh) writes window.__env__ into
 *   /usr/share/nginx/html/env-config.js from container environment variables
 *   (sourced from K8s ConfigMap / SealedSecret) before nginx starts.
 *   This allows a single, environment-agnostic image to serve all environments.
 *
 * Local development:
 *   window.__env__ is an empty object (public/env-config.js no-op).
 *   Vite injects VITE_* values via import.meta.env as normal.
 */

// Augment globalThis so TypeScript knows about __env__
declare global {
  // eslint-disable-next-line no-var
  var __env__: Record<string, string | undefined> | undefined;
}

// Placeholder constants for validation - used to detect unconfigured credentials
export const PLACEHOLDER_CLIENT_ID = 'your-client-id-here';
export const PLACEHOLDER_TENANT_ID = 'your-tenant-id-here';

/**
 * Get the current origin safely (handles SSR/build environments where window may not exist)
 */
function getOrigin(): string {
  if (globalThis.window !== undefined && globalThis.location) {
    return globalThis.location.origin;
  }
  return 'http://localhost:3000';
}

interface EnvConfig {
  // API Configuration
  apiBaseUrl: string;
  assessmentServiceBaseUrl: string;
  complianceRequirementsServiceBaseUrl: string;
  evidenceServiceBaseUrl: string;
  externalServiceBaseUrl: string;
  locationServiceBaseUrl: string;
  userServiceBaseUrl: string;
  vehicleServiceBaseUrl: string;
  checkrDashboardBaseUrl: string;
  checkrBackgroundPackageSlug: string;
  checkrMvrPackageSlug: string;

  // Authentication Service Configuration
  authServiceUrl: string;
  authClientId: string;
  authRedirectUri: string;
  authTenantId: string;
  authorizationServiceUrl: string;

  // App Configuration
  appName: string;
  appVersion: string;
  defaultLanguage: string;

  // Feature Flags
  useMockData: boolean;
  enableDevTools: boolean;
  authEnabled: boolean;
  authorizationEnabled: boolean;
  digitalFormEnabled: boolean;
  vehicleTabEnabled: boolean;
  /**
   * Serve Requirements Overview from the assessment service's persisted snapshot instead of
   * recomputing on every page load. The persisted data is kept current by the assessment event
   * pipeline and the scheduled expiration sweep, so it reflects state as of the last update rather
   * than "now" — in exchange for dropping a per-requirement evidence fan-out per page load.
   * Covers both the driver/monitor and the service-provider (SP Staff / FO) paths, which share
   * ComplianceDataContext.
   */
  usePersistedCompliance: boolean;

  // TODO: Replace with actual user role retrieval from user context once integration is complete
  // User Roles Configuration (temporary - for role-based tab filtering)
  userRoles: string[];

  // User Context Integration Feature Flag
  useUserContext: boolean;

  // Provider submitter roles for provider-scoped evidence routing
  providerSubmitterRoles: string[];

  // Orgs whose Administrator gets full Service Provider CRUD, and orgs that
  // only ever get Service Provider read access regardless of role
  serviceProviderAdminOrgs: string[];
  serviceProviderReadonlyOrgs: string[];

  // Temporary Constants (until user integration is complete)
  defaultOrgId: string;
  defaultEntityId: string;
  defaultUserEmail: string;

  // Token Refresh Configuration
  tokenRefreshThresholdSeconds: number;

  // Activity Management Configuration
  inactivityTimeoutSeconds: number;
  activityCheckIntervalSeconds: number;

  // Cache Configuration
  cacheLookupTablesMs: number;
  invalidateCacheOnMutation: boolean;

  // Analytics Configuration
  // Blank by default (local dev, and any environment that omits the ConfigMap key) - the
  // analytics bootstrap in src/utils/analytics.ts no-ops when these are empty, so pre-prod
  // stays tracking-free without any special-casing.
  gaMeasurementId: string;
  clarityProjectId: string;
}

/**
 * Get environment variable with optional default value.
 * Prefers window.__env__ (runtime injection via entrypoint.sh) over
 * import.meta.env (Vite build-time, used during local development only).
 */
function getEnvVar(key: string, defaultValue: string = ''): string {
  const runtimeValue = globalThis.__env__?.[key];
  if (runtimeValue !== undefined && runtimeValue !== '') return runtimeValue;
  return import.meta.env[key] ?? defaultValue;
}

/**
 * Get boolean environment variable
 */
function getEnvBool(key: string, defaultValue: boolean = false): boolean {
  const runtimeValue = globalThis.__env__?.[key];
  const value = (runtimeValue !== undefined && runtimeValue !== '') ? runtimeValue : import.meta.env[key];
  if (value === undefined) return defaultValue;
  return value === 'true' || value === '1';
}

/**
 * Environment configuration object
 * All environment variables are accessed through this object for type safety
 */
export const env: EnvConfig = {
  // API Configuration
  apiBaseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:5000/api'),
  assessmentServiceBaseUrl: getEnvVar('VITE_ASSESSMENT_SERVICE_BASE_URL', 'http://localhost:5011/api'),
  complianceRequirementsServiceBaseUrl: getEnvVar('VITE_COMPLIANCE_REQUIREMENTS_SERVICE_BASE_URL', 'http://localhost:5000/api'),
  evidenceServiceBaseUrl: getEnvVar('VITE_EVIDENCE_SERVICE_BASE_URL', 'http://localhost:5012/api'),
  externalServiceBaseUrl: getEnvVar('VITE_EXTERNAL_SERVICE_BASE_URL', 'http://localhost:5673/api'),
  locationServiceBaseUrl: getEnvVar('VITE_LOCATION_SERVICE_BASE_URL', 'http://localhost:5100/api'),
  userServiceBaseUrl: getEnvVar('VITE_USER_SERVICE_BASE_URL', 'http://localhost:5001/api'),
  vehicleServiceBaseUrl: getEnvVar('VITE_VEHICLE_SERVICE_BASE_URL', 'http://localhost:5002/api'),
  checkrDashboardBaseUrl: getEnvVar('VITE_CHECKR_DASHBOARD_BASE_URL', 'https://dashboard.checkrhq-staging.net'),
  checkrBackgroundPackageSlug: getEnvVar('VITE_CHECKR_BACKGROUND_PACKAGE_SLUG', 'monitor_package'),
  checkrMvrPackageSlug: getEnvVar('VITE_CHECKR_MVR_PACKAGE_SLUG', 'standalone_mvr'),

  // Authentication Service Configuration
  authServiceUrl: getEnvVar('VITE_AUTH_SERVICE_URL', 'http://localhost:9011'),
  authClientId: getEnvVar('VITE_AUTH_CLIENT_ID', PLACEHOLDER_CLIENT_ID),
  authRedirectUri: getEnvVar('VITE_AUTH_REDIRECT_URI', `${getOrigin()}/auth/callback`),
  authTenantId: getEnvVar('VITE_AUTH_TENANT_ID', PLACEHOLDER_TENANT_ID),
  authorizationServiceUrl: getEnvVar('VITE_AUTHORIZATION_SERVICE_URL', 'http://localhost:8080/api'),

  // App Configuration
  appName: getEnvVar('VITE_APP_NAME', 'Compliance Portal'),
  appVersion: getEnvVar('VITE_APP_VERSION', '1.0.0'),
  defaultLanguage: getEnvVar('VITE_DEFAULT_LANGUAGE', 'en'),

  // Feature Flags
  useMockData: getEnvBool('VITE_USE_MOCK_DATA', false),
  enableDevTools: getEnvBool('VITE_ENABLE_DEV_TOOLS', false),
  authEnabled: getEnvBool('VITE_AUTH_ENABLED', true),
  // Authorization only matters when auth is disabled; when auth is enabled, authorization is always required
  authorizationEnabled: getEnvBool('VITE_AUTHORIZATION_ENABLED', true),
  digitalFormEnabled: getEnvBool('VITE_DIGITAL_FORM_ENABLED', false),
  vehicleTabEnabled: getEnvBool('VITE_VEHICLE_TAB_ENABLED', false),
  // Off by default: opt in per environment, and flip back without a rebuild if the persisted data
  // looks wrong.
  usePersistedCompliance: getEnvBool('VITE_USE_PERSISTED_COMPLIANCE', false),

  // TODO: Replace with actual user role retrieval from user context once integration is complete
  // User Roles Configuration (temporary - for role-based tab filtering)
  // Format: comma-separated roles, e.g., "Driver,Monitor" or just "Driver"
  userRoles: getEnvVar('VITE_USER_ROLES', 'Driver,Monitor').split(',').map(role => role.trim()).filter(Boolean),

  // User Context Integration Feature Flag
  // When true: fetch user data from User Service API after login and use Redux store
  // When false: use environment variables (legacy mode)
  useUserContext: getEnvBool('VITE_USE_USER_CONTEXT', false),

  // Provider submitter roles used by evidence/compliance flow. Also flows into
  // isFieldUser, so any role listed here gets Compliance Management (not the
  // admin-side Compliance Requirements) — matching SpAdmin's design ceiling
  // of "at-least-what-SpStaff-can-do" per the role hierarchy.
  // Format: comma-separated roles, e.g., "SpStaff" or "SpStaff,SpAdmin".
  providerSubmitterRoles: getEnvVar('VITE_PROVIDER_SUBMITTER_ROLES', 'SpStaff,SpAdmin')
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean),

  // Orgs granting full Service Provider CRUD to their Administrator role.
  // Format: comma-separated org names, e.g. "Compliance,DIG".
  serviceProviderAdminOrgs: getEnvVar(
    'VITE_SERVICE_PROVIDER_ADMIN_ORGS',
    'Compliance,DIG,DriverImplementationTeam,FieldOps,ProductAndEngineering'
  )
    .split(',')
    .map((org) => org.trim())
    .filter(Boolean),

  // Orgs limited to Service Provider read access regardless of role.
  // Format: comma-separated org names, e.g. "SMT".
  serviceProviderReadonlyOrgs: getEnvVar('VITE_SERVICE_PROVIDER_READONLY_ORGS', 'SMT')
    .split(',')
    .map((org) => org.trim())
    .filter(Boolean),


  // Temporary Constants (until user integration is complete)
  // NOTE: These default UUIDs are placeholders - set actual values in .env file
  defaultOrgId: getEnvVar('VITE_DEFAULT_ORG_ID', '00000000-0000-0000-0000-000000000000'),
  defaultEntityId: getEnvVar('VITE_DEFAULT_ENTITY_ID', '00000000-0000-0000-0000-000000000000'),
  defaultUserEmail: getEnvVar('VITE_USER_EMAIL', 'user@example.com'),

  // Cache Configuration
  cacheLookupTablesMs: Number(getEnvVar('VITE_CACHE_LOOKUP_TABLES_MS', String(5 * 60 * 1000))),
  invalidateCacheOnMutation: getEnvBool('VITE_INVALIDATE_CACHE_ON_MUTATION', false),

  // Token Refresh Configuration
  // How many seconds before expiry to trigger auto-refresh (both proactive and emergency)
  //
  // Environment-specific thresholds:
  // - Dev: 30 seconds (tokens expire in 2 min = 120s)
  //   Rationale: Shorter tokens for faster testing, 25% buffer (30/120) for refresh
  // - Prod: 300 seconds (tokens expire in 60 min = 3600s)
  //   Rationale: 5 minutes provides 8.3% buffer (300/3600), balancing security with UX
  //   Users get seamless token refresh while minimizing refresh frequency
  tokenRefreshThresholdSeconds: Number(getEnvVar('VITE_TOKEN_REFRESH_THRESHOLD_SECONDS', '30')),

  // Activity Management Configuration
  inactivityTimeoutSeconds: Number(getEnvVar('VITE_INACTIVITY_TIMEOUT_SECONDS', '1800')),
  activityCheckIntervalSeconds: Number(getEnvVar('VITE_ACTIVITY_CHECK_INTERVAL_SECONDS', '30')),

  // Analytics Configuration
  gaMeasurementId: getEnvVar('VITE_GA_MEASUREMENT_ID', ''),
  clarityProjectId: getEnvVar('VITE_CLARITY_PROJECT_ID', ''),
};

/**
 * Type-safe keys for string properties only
 */
type StringConfigKeys = {
  [K in keyof EnvConfig]: EnvConfig[K] extends string ? K : never;
}[keyof EnvConfig];

/**
 * Validate required environment variables
 * Call this at app startup to ensure all required config is present
 */
export function validateEnv(): void {
  const missing: string[] = [];

  // Always required variables (type-safe to only string keys)
  const alwaysRequired: readonly StringConfigKeys[] = ['apiBaseUrl'] as const;
  
  // Authentication-specific variables (only required when auth is enabled)
  const authRequired: readonly StringConfigKeys[] = [
    'authServiceUrl',
    'authClientId', 
    'authTenantId'
  ] as const;

  // Helper function for type-safe string validation
  const isValidString = (value: string): boolean => {
    return value !== '' &&
           value !== PLACEHOLDER_CLIENT_ID &&
           value !== PLACEHOLDER_TENANT_ID;
  };

  // Check always required variables
  alwaysRequired.forEach((key) => {
    const value = env[key];
    if (!value || !isValidString(value)) {
      missing.push(key);
    }
  });

  // Check auth-specific variables only when auth is enabled
  if (env.authEnabled) {
    authRequired.forEach((key) => {
      const value = env[key];
      if (!value || !isValidString(value)) {
        missing.push(key);
      }
    });
  }

  if (missing.length > 0) {
    const message = `Missing or invalid environment variables: ${missing.join(', ')}.`;
    
    // In production, throw an error for missing auth variables
    if (isProd && env.authEnabled && authRequired.some(key => missing.includes(key))) {
      throw new Error(`${message} Authentication cannot function without these variables.`);
    }
    
    // In development, just warn
    console.warn(`${message} ${env.authEnabled ? 'Authentication may not work properly.' : 'Using defaults.'}`);
  }

  // Validate numeric configuration values
  const numericValidations = [
    { key: 'tokenRefreshThresholdSeconds', value: env.tokenRefreshThresholdSeconds, min: 1 },
    { key: 'inactivityTimeoutSeconds', value: env.inactivityTimeoutSeconds, min: 1 },
    { key: 'activityCheckIntervalSeconds', value: env.activityCheckIntervalSeconds, min: 1 },
  ];

  const invalidNumeric: string[] = [];
  numericValidations.forEach(({ key, value, min }) => {
    if (Number.isNaN(value) || value < min) {
      invalidNumeric.push(`${key} (must be >= ${min}, got: ${value})`);
    }
  });

  if (invalidNumeric.length > 0) {
    const numericMessage = `Invalid numeric configuration: ${invalidNumeric.join(', ')}`;
    if (isProd) {
      throw new Error(numericMessage);
    }
    console.warn(numericMessage);
  }
}

/**
 * Check if running in development mode
 */
export const isDev = import.meta.env.DEV;

/**
 * Check if running in production mode
 */
export const isProd = import.meta.env.PROD;

export default env;
